// Growth spurts (VS study — the level-up cadence). Earning DNA fills a
// per-life meter; each threshold crossing pauses the game for a pick-1-of-3
// mutation choice (ui/growth-spurt.ts). Thresholds escalate geometrically so
// early life drips choices fast and late life spaces them out.
//
// Own-orb retrieval DNA is excluded — reclaiming your dropped DNA is not new
// income, and counting it would let death farm choice moments.

import type { Entity, MutationKind } from './components'
import { ALL_MUTATION_KINDS } from './mutations'
import { POWER_DEFS, powerLevel, poolAtTier, type PowerId } from './powers'
import { currentTier } from './evolution'
import { T } from './tunables'

// A spurt card offers either a mutation stack or an adaptation level (new at
// L1 if unheld, +1 if held). Pool membership is a function of evolution tier
// (amoeba-unification.md) — evolving expands what you can become.
export type SpurtChoice =
  | { type: 'mutation'; kind: MutationKind }
  | { type: 'power'; id: PowerId }

function ensureSpurt(player: Entity): NonNullable<Entity['spurt']> {
  player.spurt ??= {
    earned: 0,
    prevThreshold: 0,
    nextThreshold: T.spurt.firstThreshold,
    pending: false,
  }
  return player.spurt
}

/** Credit earned DNA toward the next spurt. Call wherever DNA is granted. */
export function addSpurtProgress(player: Entity, amount: number): void {
  const s = ensureSpurt(player)
  s.earned += amount
  if (s.earned >= s.nextThreshold) s.pending = true
}

export function spurtPending(player: Entity): boolean {
  return player.spurt?.pending === true
}

/** DNA still to eat before the next growth spurt (HUD countdown). */
export function spurtRemaining(player: Entity): number {
  const s = player.spurt
  if (!s) return T.spurt.firstThreshold
  return Math.max(0, Math.ceil(s.nextThreshold - s.earned))
}

/** 0..1 fill toward the next spurt, for the HUD meter. */
export function spurtProgress(player: Entity): number {
  const s = player.spurt
  if (!s) return 0
  const span = s.nextThreshold - s.prevThreshold
  if (span <= 0) return 0
  return Math.max(0, Math.min(1, (s.earned - s.prevThreshold) / span))
}

/** Advance to the next threshold. A backlog of earned DNA can re-arm
 *  pending immediately (one big orb may buy two choices back to back). */
export function consumeSpurt(player: Entity): void {
  const s = ensureSpurt(player)
  s.prevThreshold = s.nextThreshold
  s.nextThreshold = Math.round(s.nextThreshold * T.spurt.thresholdGrowth)
  s.pending = s.earned >= s.nextThreshold
}

/** Up to `n` distinct cards: mutations with stack room + tier-pool
 *  adaptations under max level, shuffled together. */
export function sampleSpurtChoices(player: Entity, n = 3): SpurtChoice[] {
  const stacks = player.mutations?.stacks ?? {}
  const open: SpurtChoice[] = ALL_MUTATION_KINDS
    .filter(k => (stacks[k] ?? 0) < T.mutations.maxStacks)
    .map(kind => ({ type: 'mutation' as const, kind }))
  for (const id of poolAtTier(currentTier(player))) {
    if (powerLevel(player, id) < POWER_DEFS[id].maxLevel) {
      open.push({ type: 'power', id })
    }
  }
  for (let i = open.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[open[i], open[j]] = [open[j], open[i]]
  }
  return open.slice(0, n)
}
